import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import PageHeader from '@components/shared/PageHeader';
import {
  downloadLegacyV0UserExcel,
  exportLegacyV0UserMigrationJson,
  fetchLegacyV0User,
  saveLegacyV0UserMigrationExport,
  updateLegacyV0UserMigrationStatus,
} from '../services/legacyV0UserManager';
import {
  formatV0Date,
  isAbbonamentoActive,
  parseV0Date,
  todayYyyymmdd,
} from './legacyV0/v0DateUtils';

export default function AdminLegacyV0UserDetail() {
  const { id } = useParams();
  const userId = id ? Number(id) : null;
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'legacy-v0-user', userId],
    queryFn: () => fetchLegacyV0User(userId!),
    enabled: Boolean(userId && Number.isFinite(userId)),
  });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const titleName =
    (data?.cliente?.display_name as string | undefined) ||
    data?.email ||
    `Cliente #${id}`;
  const migrationStatus = data?.migration?.status ?? 'pending';
  const isIgnored = migrationStatus === 'ignored';

  const today = todayYyyymmdd();
  const abbonamentiSorted = useMemo(() => {
    const rows = data?.abbonamenti ?? [];
    return [...rows].sort((a, b) => {
      const aActive = isAbbonamentoActive(a.data_fine, today) ? 1 : 0;
      const bActive = isAbbonamentoActive(b.data_fine, today) ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;
      const aFine = parseV0Date(a.data_fine) ?? 0;
      const bFine = parseV0Date(b.data_fine) ?? 0;
      return bFine - aFine;
    });
  }, [data?.abbonamenti, today]);
  const activeAbbonamentiCount = abbonamentiSorted.filter((row) =>
    isAbbonamentoActive(row.data_fine, today)
  ).length;
  const mappedAbbonamentiCount = data?.abbonamenti_summary?.mapped ?? 0;
  const skippedAbbonamentiCount = data?.abbonamenti_summary?.skipped ?? 0;

  const clienteOculus = data?.cliente
    ? {
        oculus_id: data.cliente.oculus_id,
        oculus_user: data.cliente.oculus_user,
        oculus_key: data.cliente.oculus_key,
      }
    : null;
  const hasClienteOculus = Boolean(
    clienteOculus &&
      [clienteOculus.oculus_id, clienteOculus.oculus_user, clienteOculus.oculus_key].some(
        (v) => v != null && String(v).trim() !== ''
      )
  );

  const refresh = async () => {
    if (!userId) return;
    await queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-v0-user', userId] });
    await refetch();
  };

  const handleSetStatus = async (status: 'ignored' | 'pending') => {
    if (!userId) return;
    setSaving(true);
    try {
      await updateLegacyV0UserMigrationStatus(userId, status, note.trim() || null);
      notifications.show({
        color: 'green',
        message: status === 'ignored' ? 'Marked ignored' : 'Back to migrate queue',
      });
      await refresh();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Failed to update status',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportJson = async () => {
    if (!userId) return;
    setExporting(true);
    try {
      const payload = await exportLegacyV0UserMigrationJson(userId);
      const source = (payload.source ?? {}) as {
        abbonamenti?: Array<Record<string, unknown>>;
        oculus?: unknown[];
      };
      const abb = source.abbonamenti ?? [];
      const mapped = abb.filter(
        (row) => (row.product_remap as { action?: string } | undefined)?.action === 'map'
      );
      const skipped = abb.filter(
        (row) => (row.product_remap as { action?: string } | undefined)?.action === 'skip'
      );
      const saved = await saveLegacyV0UserMigrationExport(payload);
      notifications.show({
        color: 'blue',
        message: `Saved to ${saved.path} · ${abb.length} abb (${mapped.length} migrate / ${skipped.length} skip) · ${(source.oculus ?? []).length} devices`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Export failed',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!userId) return;
    setExportingExcel(true);
    try {
      const saved = await downloadLegacyV0UserExcel(userId);
      notifications.show({
        color: 'teal',
        message: `Downloaded ${saved.filename}`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Excel export failed',
      });
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title={titleName}
          subtitle={`Legacy v0 WordPress cliente post_id #${id}`}
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Legacy v0 users', href: '/admin/legacy-v0-users' },
            { label: String(id ?? '') },
          ]}
          backUrl='/admin/legacy-v0-users'
          actions={
            <Group>
              <Badge color={data?.email_ok ? 'teal' : 'orange'} variant='light'>
                {data?.email_ok
                  ? 'email ok'
                  : data?.email_is_fallback
                    ? 'fallback email'
                    : 'no email'}
              </Badge>
              {data ? (
                <>
                  <Badge color='blue' variant='light'>
                    {mappedAbbonamentiCount} → migrate
                  </Badge>
                  <Badge color='teal' variant='light'>
                    {activeAbbonamentiCount} abb. attivi
                  </Badge>
                  <Badge color='gray' variant='light'>
                    {data.oculus.length} devices
                  </Badge>
                </>
              ) : null}
              <Badge color='grape' variant='light'>
                {migrationStatus}
              </Badge>
            </Group>
          }
        />

        {isLoading ? (
          <Group justify='center' py='xl'>
            <Loader />
          </Group>
        ) : error ? (
          <Text c='red'>{(error as Error).message}</Text>
        ) : !data ? (
          <Text c='dimmed'>Not found</Text>
        ) : (
          <Stack gap='md'>
            {!data.email_ok ? (
              <Alert color='orange' title='No WP email — using fallback'>
                Missing `utente_wp` → `wpb_users` email. Temporary address:{' '}
                <Text span ff='monospace' size='sm'>
                  {data.email}
                </Text>
                . Still listed under filter “no email” (`email_ok=false`).
              </Alert>
            ) : null}

            {abbonamentiSorted.length > 0 && mappedAbbonamentiCount === 0 ? (
              <Alert color='orange' title='Nessun prodotto be2025 da migrare'>
                Questo cliente ha {abbonamentiSorted.length} abbonamento/i v0, ma tutti
                sono skip ({skippedAbbonamentiCount}): categorie fuori dalla mappa
                WELLBEING / RILASSAMENTO / PSYCHOLOGY / SITUATIONS / STRESS / CHANGE. I{' '}
                {data.oculus.length} device oculus restano nel JSON sotto{' '}
                <Text span ff='monospace' size='sm'>
                  source.oculus
                </Text>
                ; la tabella “Product map” in fondo è solo le regole globali, non i
                suoi prodotti.
              </Alert>
            ) : null}

            <Paper withBorder p='md' radius='md'>
              <Group justify='space-between' mb='sm'>
                <Title order={4}>Abbonamenti</Title>
                <Group gap='xs'>
                  <Badge variant='light' color='teal'>
                    {activeAbbonamentiCount} attivi
                  </Badge>
                  <Badge variant='light' color='blue'>
                    {mappedAbbonamentiCount} → migrate
                  </Badge>
                  <Badge variant='light' color='orange'>
                    {skippedAbbonamentiCount} skip
                  </Badge>
                  <Badge variant='light' color='gray'>
                    {abbonamentiSorted.length} totali
                  </Badge>
                </Group>
              </Group>
              <Alert color='gray' variant='light' mb='sm'>
                Migrate WELLBEING / RILASSAMENTO / PSYCHOLOGY / SITUATIONS; STRESS +
                CHANGE → Corporate. Labels with DEMO strip to the domain product when
                it exists in v2. Anything else is skipped (no UserProduct in DB).
              </Alert>
              {abbonamentiSorted.length === 0 ? (
                <Text c='dimmed' size='sm'>
                  Nessun abbonamento
                </Text>
              ) : (
                <Table withTableBorder striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Stato</Table.Th>
                      <Table.Th>Prodotto v0</Table.Th>
                      <Table.Th>→ be2025</Table.Th>
                      <Table.Th>Inizio</Table.Th>
                      <Table.Th>Scadenza</Table.Th>
                      <Table.Th>EULA</Table.Th>
                      <Table.Th>Kiosk</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {abbonamentiSorted.map((row) => {
                      const active = isAbbonamentoActive(row.data_fine, today);
                      const remap = row.product_remap;
                      const willMap = remap?.action === 'map';
                      return (
                        <Table.Tr key={String(row.id)}>
                          <Table.Td>
                            <Badge size='sm' variant='light' color={active ? 'teal' : 'gray'}>
                              {active ? 'attivo' : 'scaduto'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size='sm' fw={600}>
                              {String(row.categoria_titolo || '—')}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              cat post_id {String(row.categoria_post_id || '—')}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {willMap ? (
                              <>
                                <Badge size='sm' variant='light' color='blue'>
                                  {String(remap?.be_product_name)}
                                </Badge>
                                {remap?.note ? (
                                  <Text size='xs' c='dimmed' mt={2}>
                                    {remap.note}
                                  </Text>
                                ) : null}
                              </>
                            ) : (
                              <Badge size='sm' variant='light' color='orange'>
                                skip
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td>{formatV0Date(row.data_inizio)}</Table.Td>
                          <Table.Td>
                            <Text
                              size='sm'
                              fw={active ? 700 : 400}
                              c={active ? undefined : 'dimmed'}
                            >
                              {formatV0Date(row.data_fine)}
                            </Text>
                          </Table.Td>
                          <Table.Td>{String(row.accettazione_eula || '—')}</Table.Td>
                          <Table.Td>{String(row.kiosk_mode || '—')}</Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>

            <Paper withBorder p='md' radius='md'>
              <Group justify='space-between' mb='sm'>
                <Title order={4}>Devices (oculus)</Title>
                <Badge variant='light' color='blue'>
                  {data.oculus.length} assegnati
                </Badge>
              </Group>
              {hasClienteOculus ? (
                <Alert color='blue' variant='light' mb='sm' title='Oculus su riga cliente'>
                  <Text size='sm'>
                    id: {String(clienteOculus?.oculus_id || '—')} · user:{' '}
                    {String(clienteOculus?.oculus_user || '—')} · key:{' '}
                    {String(clienteOculus?.oculus_key || '—')}
                  </Text>
                </Alert>
              ) : null}
              {data.oculus.length === 0 ? (
                <Text c='dimmed' size='sm'>
                  Nessun device in tabella oculus
                </Text>
              ) : (
                <Table withTableBorder striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Oculus ID</Table.Th>
                      <Table.Th>Oculus user</Table.Th>
                      <Table.Th>Cliente titolo</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.oculus.map((row) => (
                      <Table.Tr key={String(row.id)}>
                        <Table.Td>
                          <Text size='sm' ff='monospace' fw={600}>
                            {String(row.oculus_id || '—')}
                          </Text>
                        </Table.Td>
                        <Table.Td>{String(row.oculus_user || '—')}</Table.Td>
                        <Table.Td>{String(row.cliente_titolo || '—')}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>

            <Stack gap='sm'>
              <Title order={3}>Migration decision</Title>
              <Textarea
                placeholder='Optional note'
                value={note}
                onChange={(event) => setNote(event.currentTarget.value)}
                minRows={2}
              />
              <Group>
                <Button
                  color='blue'
                  variant='light'
                  loading={exporting}
                  disabled={exportingExcel}
                  onClick={handleExportJson}
                >
                  Export JSON
                </Button>
                <Button
                  color='teal'
                  variant='light'
                  loading={exportingExcel}
                  disabled={exporting}
                  onClick={handleExportExcel}
                >
                  Export Excel
                </Button>
                {isIgnored ? (
                  <Button
                    variant='light'
                    loading={saving}
                    onClick={() => handleSetStatus('pending')}
                  >
                    Undo ignore
                  </Button>
                ) : (
                  <Button
                    color='gray'
                    loading={saving}
                    onClick={() => handleSetStatus('ignored')}
                  >
                    Mark as ignored
                  </Button>
                )}
              </Group>
            </Stack>

            <Title order={4}>Profile (clienti)</Title>
            <Table withTableBorder>
              <Table.Tbody>
                {Object.entries(data.cliente)
                  .filter(([key]) => !['display_name'].includes(key))
                  .slice(0, 30)
                  .map(([key, value]) => (
                    <Table.Tr key={key}>
                      <Table.Td w={220}>
                        <Text size='sm' fw={600}>
                          {key}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm' style={{ wordBreak: 'break-word' }}>
                          {value == null || value === '' ? '—' : String(value)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>

            <Title order={4}>WP user</Title>
            {data.wp_user ? (
              <Table withTableBorder>
                <Table.Tbody>
                  {Object.entries(data.wp_user).map(([key, value]) => (
                    <Table.Tr key={key}>
                      <Table.Td w={220}>
                        <Text size='sm' fw={600}>
                          {key}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>{value == null ? '—' : String(value)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c='dimmed' size='sm'>
                No linked wp_users row
              </Text>
            )}

            <Title order={4}>Usermeta (billing / profile)</Title>
            <Table withTableBorder>
              <Table.Tbody>
                {Object.entries(data.usermeta).map(([key, value]) => (
                  <Table.Tr key={key}>
                    <Table.Td w={220}>
                      <Text size='sm' fw={600}>
                        {key}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm' style={{ wordBreak: 'break-word' }}>
                        {value == null || value === '' ? '—' : value}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {data.product_map?.length ? (
              <>
                <Title order={4}>Regole remap globali (v0 → be2025)</Title>
                <Text size='sm' c='dimmed' mb='xs'>
                  Documentazione delle categorie migrabili — non sono i prodotti di
                  questo cliente. I suoi abbonamenti sono nella tabella sopra.
                </Text>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>v0 categoria</Table.Th>
                      <Table.Th>be2025 product</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.product_map.map((row) => (
                      <Table.Tr key={row.source}>
                        <Table.Td>
                          <Text size='sm'>{row.source}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size='sm'>{row.target}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : null}

            {data.field_map?.length ? (
              <>
                <Title order={4}>Field map → be2025</Title>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>source</Table.Th>
                      <Table.Th>target</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.field_map.map((row) => (
                      <Table.Tr key={row.source}>
                        <Table.Td>
                          <Text size='sm'>{row.source}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size='sm'>{row.target}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
